/**
 * TOCToolbar Component
 *
 * Hover-triggered toolbar for controlling which heading levels are included
 * in the Table of Contents. Provides checkboxes for H1-H6 heading levels.
 *
 * Features:
 * - Checkbox controls for each heading level (H1-H6)
 * - Default selection: H2 and H3
 * - Immediate TOC refresh on selection change
 * - Notion-style horizontal layout
 * - Clear visual feedback
 * - Full keyboard accessibility
 * - Screen reader support with ARIA labels
 * - Responsive design for mobile and desktop
 *
 * Props:
 * - includedLevels: Array of currently selected heading levels (e.g., [2, 3])
 * - onLevelsChange: Callback function when level selection changes
 *
 * Usage:
 *   <TOCToolbar
 *     includedLevels={[2, 3]}
 *     onLevelsChange={(newLevels) => updateBlock(...)}
 *   />
 *
 * Related Files:
 * - app/javascript/components/blocknote/TableOfContentsBlock.jsx
 */

import React from "react";

/**
 * Main TOCToolbar Component
 *
 * Renders a horizontal toolbar with checkboxes for heading level selection.
 * Fully accessible with keyboard navigation and screen reader support.
 *
 * @param {Object} props
 * @param {Array<number>} props.includedLevels - Array of included heading levels (1-6)
 * @param {Function} props.onLevelsChange - Callback when levels change
 */
export default function TOCToolbar({
  includedLevels = [2, 3],
  onLevelsChange,
}) {
  /**
   * Handles checkbox change for a specific heading level
   *
   * Adds or removes the level from the includedLevels array and
   * calls the onLevelsChange callback with the updated array.
   * Ensures at least one level is always selected.
   *
   * @param {number} level - The heading level (1-6)
   * @param {boolean} isChecked - Whether the checkbox is checked
   */
  const handleLevelToggle = (level, isChecked) => {
    let newLevels;

    if (isChecked) {
      // Add level to array if not already present
      if (!includedLevels.includes(level)) {
        newLevels = [...includedLevels, level].sort((a, b) => a - b);
      } else {
        newLevels = includedLevels;
      }
    } else {
      // Remove level from array
      newLevels = includedLevels.filter((l) => l !== level);

      // Ensure at least one level is always selected
      if (newLevels.length === 0) {
        console.warn("At least one heading level must be selected");
        return;
      }
    }

    onLevelsChange(newLevels);
  };

  /**
   * Handle keyboard navigation within toolbar
   *
   * Arrow keys to move between checkboxes
   * Space to toggle checkbox
   */
  const handleKeyDown = (e, level) => {
    const checkboxes = document.querySelectorAll(
      '.toc-toolbar input[type="checkbox"]',
    );
    const currentIndex = Array.from(checkboxes).findIndex(
      (cb) => cb.id === `toc-level-${level}`,
    );

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < checkboxes.length - 1) {
          checkboxes[currentIndex + 1].focus();
        }
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          checkboxes[currentIndex - 1].focus();
        }
        break;
      case "Home":
        e.preventDefault();
        checkboxes[0].focus();
        break;
      case "End":
        e.preventDefault();
        checkboxes[checkboxes.length - 1].focus();
        break;
    }
  };

  // All possible heading levels
  const headingLevels = [1, 2, 3, 4, 5, 6];

  return (
    <div
      className="toc-toolbar bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5 sm:py-2 mb-3"
      role="toolbar"
      aria-label="Table of contents heading level settings"
      aria-controls="toc-content"
    >
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
        <span className="text-xs font-medium text-neutral-700 uppercase tracking-wide">
          Include:
        </span>

        <div
          className="flex items-center gap-2 sm:gap-3 flex-wrap"
          role="group"
          aria-label="Select heading levels to include in table of contents"
        >
          {headingLevels.map((level) => {
            const isChecked = includedLevels.includes(level);
            const checkboxId = `toc-level-${level}`;
            const isLastSelected = isChecked && includedLevels.length === 1;

            return (
              <label
                key={level}
                htmlFor={checkboxId}
                className={`
                  flex items-center gap-1.5 cursor-pointer text-sm
                  transition-colors
                  ${
                    isChecked
                      ? "text-neutral-900 font-medium"
                      : "text-neutral-600 hover:text-neutral-800"
                  }
                  ${isLastSelected ? "cursor-not-allowed opacity-75" : ""}
                `}
                title={
                  isLastSelected
                    ? "At least one level must be selected"
                    : `${isChecked ? "Exclude" : "Include"} heading level ${level}`
                }
              >
                <input
                  type="checkbox"
                  id={checkboxId}
                  checked={isChecked}
                  onChange={(e) => handleLevelToggle(level, e.target.checked)}
                  onKeyDown={(e) => handleKeyDown(e, level)}
                  disabled={isLastSelected}
                  className={`
                    h-4 w-4 rounded border-gray-300 text-blue-600
                    focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                    transition-colors
                    ${isLastSelected ? "cursor-not-allowed" : "cursor-pointer"}
                  `}
                  aria-label={`${isChecked ? "Exclude" : "Include"} heading level ${level}`}
                  aria-describedby={
                    isLastSelected ? `${checkboxId}-description` : undefined
                  }
                />
                <span className="font-medium select-none">H{level}</span>
                {isLastSelected && (
                  <span id={`${checkboxId}-description`} className="sr-only">
                    At least one heading level must be selected
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
